import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI } from '../../services/api';
import Card from '../common/Card';
import Loading from '../common/Loading';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import { formatDate, handleApiError, getImageUrl, showToast, showConfirm } from '../../utils/helpers';
import { POST_STATUS } from '../../utils/constants';

const TrashBinPage = () => {
  const { user, isAdmin } = useAuth();
  const { isRTL, isArabic } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoringPostId, setRestoringPostId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const postsResponse = await postsAPI.getDeleted();
      setPosts(postsResponse.data.posts);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (postId) => {
    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من استعادة هذا البوست؟' : 'Are you sure you want to restore this post?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setRestoringPostId(postId);
      await postsAPI.restore(postId);
      await fetchData();
      showToast.success(isRTL ? 'تم استعادة البوست بنجاح!' : 'Post restored successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setRestoringPostId(null);
    }
  };

  const getDaysUntilPermanentDelete = (deletedAt) => {
    if (!deletedAt) return null;
    const deleted = new Date(deletedAt);
    const now = new Date();
    const daysPassed = Math.floor((now - deleted) / (1000 * 60 * 60 * 24));
    const daysRemaining = 3 - daysPassed;
    return daysRemaining > 0 ? daysRemaining : 0;
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchData} variant="secondary">
          {isRTL ? 'إعادة المحاولة' : 'Try Again'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isRTL ? 'سلة المحذوفات' : 'Trash Bin'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isRTL 
              ? `تم العثور على ${posts.length} ${posts.length === 1 ? 'بوست محذوف' : 'بوست محذوف'}`
              : `${posts.length} ${posts.length === 1 ? 'deleted post' : 'deleted posts'} found`}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {isRTL 
              ? 'سيتم حذف البوستات نهائياً بعد 3 أيام من تاريخ الحذف'
              : 'Posts will be permanently deleted after 3 days'}
          </p>
        </div>
      </div>

      {posts.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <TrashIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isRTL ? 'سلة المحذوفات فارغة' : 'Trash bin is empty'}
            </h3>
            <p className="text-gray-500">
              {isRTL 
                ? 'لا توجد بوستات محذوفة حالياً'
                : 'No deleted posts at the moment'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => {
            const daysRemaining = getDaysUntilPermanentDelete(post.deleted_at);

            return (
              <Card key={post.id} className="p-4">
                <div className="flex items-center gap-4">
                  {/* Image Thumbnail */}
                  <Link to={`/posts/${post.id}`} className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden">
                      {post.current_image_url ? (
                        <img
                          src={getImageUrl(post.current_image_url)}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                          {isRTL ? 'لا صورة' : 'No image'}
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Post Info */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/posts/${post.id}`}>
                      <h3 className="font-semibold text-gray-900 truncate mb-1 hover:text-primary-600">
                        {post.title || (isRTL ? 'بدون عنوان' : 'No title')}
                      </h3>
                    </Link>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{post.client_name}</span>
                      <span>•</span>
                      <span>{formatDate(post.deleted_at)}</span>
                    </div>
                  </div>

                  {/* Status & Days */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <StatusBadge status={post.status} />
                    
                    {/* Days remaining badge */}
                    {daysRemaining !== null && (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        daysRemaining === 0 
                          ? 'bg-red-100 text-red-800' 
                          : daysRemaining === 1 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {isRTL 
                          ? `${daysRemaining === 0 ? 'سيتم الحذف اليوم' : daysRemaining === 1 ? 'يوم واحد' : `${daysRemaining} أيام`}`
                          : `${daysRemaining === 0 ? 'Deleting today' : daysRemaining === 1 ? '1 day' : `${daysRemaining} days`}`}
                      </span>
                    )}

                    {/* Restore Button */}
                    {isAdmin && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRestore(post.id)}
                        disabled={restoringPostId === post.id}
                      >
                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                        {isRTL ? 'استعادة' : 'Restore'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TrashBinPage;
